import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TriggerCreate, TriggerForEditor } from "@dots-projects/types";
import TriggerFlow from "./TriggerFlow";
import { useTrigger, useTriggerMutation } from "./useTrigger";
import { triggerEditorReducer } from "./reducer/triggerEditorReducer";
import {
  TriggerEditorState,
  TriggerEditorAction,
  OnPersistTrigger,
  PersistKind,
} from "./reducer/reducerTypes";
import { Thunk, useThunkReducer } from "./reducer/useThunkReducer";
import { formatDistanceToNow, parseISO } from "date-fns";
import "./style.css";
import { PropsPanel } from "./PropsPanel";
import { ControlRow } from "./ControlRow";
import { Modals } from "./Modals";
import TriggerEditorErrorHandler from "./TriggerEditorErrorHandler";
import { ValidationErrorsBox } from "./ValidationErrorsBox";
import { saveDraft, updateTriggerName } from "./reducer/actionCreators";
import { getSupaClient } from "../../lib/supabase";
import { useLocation, useNavigate } from "react-router-dom";
import { EditOutlined, LineChartOutlined } from "@ant-design/icons";
import { useDefinedPreviousValue } from "./useDefinedPreviousValue";
import clsx from "clsx";
import {
  TriggerError,
  TriggerErrorPayload,
} from "@dots-projects/utils/exceptions";

import "./TriggerEditor.css";
import { Spin } from "antd";
import DiscordTriggerTrack from "../DiscordTriggerTrack/DiscordTriggerTrack";
import { DiscordTriggersRpc } from "../../lib/discordTypes";
import Paywall from "../Paywall/Paywall";
import { usePaywallStateDispatch } from "../Paywall/PaywallContext";
import React from "react";
import { ErrorBoundary } from "./ErrorBoundary";

const TriggerEditorContext = createContext<{
  state: TriggerEditorState;
  dispatch: Dispatch<
    TriggerEditorAction | Thunk<TriggerEditorAction, TriggerEditorState>
  >;
  onPersist: OnPersistTrigger;
  validationErrors?: string[];
}>({
  state: {} as TriggerEditorState,
  dispatch: () => {},
  onPersist: (() => {}) as unknown as OnPersistTrigger,
  validationErrors: [],
});

export function useTriggerEditorStateDispatch() {
  return useContext(TriggerEditorContext);
}

type TriggerEditorFrontendProps = {
  initialTrigger: TriggerForEditor;
  onPersist: OnPersistTrigger;
  isLoadingError: boolean;
  initialValidationErrors: { message: string }[] | TriggerErrorPayload[] | null;
  initialLiveTriggerId: string;
  error: { message: string } | TriggerErrorPayload | null;
  removeControls?: boolean;
  resizeMiniMap?: boolean;
  setCurrentTrigger?: Dispatch<React.SetStateAction<TriggerForEditor | null>>;
};

/**
 * The main copmonent for the trigger editor. This component creates the reducer context and passes state, dispatch and onPersist downstream.
 *
 * We separate TriggerEditorFrontend from TriggerEditor for easier testing (especially in the absence of backend endpoints).
 */
function TriggerEditorFrontendInternal({
  initialTrigger,
  onPersist,
  isLoadingError,
  initialValidationErrors,
  error,
  removeControls = false,
  resizeMiniMap = false,
  setCurrentTrigger,
}: TriggerEditorFrontendProps) {
  const supabase = getSupaClient();

  const [state, dispatch] = useThunkReducer(triggerEditorReducer, {
    trigger: { past: [], future: [], current: initialTrigger },
    flow: { nodes: [], edges: [] },
    editor: {
      selection: [],
      isPersisting: false,
      latestPersistResponses: {
        publish: undefined,
        "save-draft": undefined,
        "try-draft": undefined,
        "view-draft": undefined,
      },
    },
  });

  if (setCurrentTrigger) {
    setCurrentTrigger(state.trigger.current);
  }

  useEffect(() => {
    dispatch({ type: "$$init_trigger_layout" });
  }, [initialTrigger.id]);

  const errors = useMemo(() => {
    if (error instanceof TriggerError) {
      return [error.data];
    } else if (error instanceof Error) {
      return [
        {
          message: error.message,
        },
      ];
    } else if (isLoadingError) {
      return [
        {
          message: "Failed to load the trigger. Please try again later.",
        },
      ];
    }
    return [];
  }, [isLoadingError, error]);

  // validationErrors are set using useMemo so that error modal is only displayed the first time the error obtained
  const validationErrors: { message: string }[] | null = useMemo(() => {
    // This function filters out all validation errors of the status object. It only returns errors after saving an action
    const getValidationErrors = (state: TriggerEditorState) => {
      const errorsSaveDraft: string[] | undefined = state.editor
        .latestPersistResponses["save-draft"]
        ? state.editor.latestPersistResponses["save-draft"]["validationErrors"]
        : undefined;
      if (errorsSaveDraft === undefined) return undefined;
      return [
        ...errorsSaveDraft.map((e) =>
          typeof e === "object" ? e : { message: e }
        ),
      ];
    };

    const currentValidationErrors = getValidationErrors(state);
    // if there are validation errors from a recent edit, display them. else, display initial errors from trigger load.
    if (currentValidationErrors !== undefined) {
      return currentValidationErrors;
    } else {
      return initialValidationErrors;
    }
  }, [state.editor.latestPersistResponses, initialValidationErrors]);

  // If an error occur we need to reset the state to editor in order to perform any actions on the trigger.
  useEffect(() => {
    if (isLoadingError) {
      dispatch({
        type: "$$update_is_persisting",
        payload: false,
      });
    }
  }, [isLoadingError]);

  // Close errors modal if there are no errors
  useEffect(() => {
    if (validationErrors?.length || errors?.length) {
      dispatch({
        type: "show_errors_box",
        payload: { errorBoxId: "validation-errors" },
      });
    } else {
      // don't close error box if unsupported warning is being displayed
      if (state.editor.visibleErrorBoxId !== "unsupported-step") {
        dispatch({ type: "hide_errors_box" });
      }
    }
  }, [validationErrors, errors]);

  const triggerErrors = useMemo(() => {
    return [...errors, ...(validationErrors ? validationErrors : [])];
  }, [errors, validationErrors]);

  // This function updates the trigger name
  const changeTriggerName = async (rootId: string, newName: string) => {
    const { data: res, error } = await supabase
      .from("triggers")
      .update([
        {
          name: newName,
        },
      ])
      .in("id", [rootId]);

    if (error) {
      console.log("error");
      console.log(error);
    }

    dispatch(updateTriggerName(newName));
    dispatch(saveDraft(onPersist));
  };

  return (
    <TriggerEditorContext.Provider
      value={{
        state,
        dispatch,
        onPersist,
      }}
    >
      <div className="relative rounded-lg border border-gray-200 overflow-hidden h-full">
        {/* <div className="relative h-full"> */}
        <TriggerFlow
          nodes={state.flow.nodes}
          edges={state.flow.edges}
          moveControls={!!state.editor.visibleErrorBoxId}
          resizeMinimap={resizeMiniMap}
        />
        {!removeControls && (
          <>
            <ControlRow />
            <PropsPanel />
            <Modals changeTriggerName={changeTriggerName} />
            <ValidationErrorsBox validationErrors={triggerErrors} />
          </>
        )}
      </div>
    </TriggerEditorContext.Provider>
  );
}

export function TriggerEditorFrontend(props: TriggerEditorFrontendProps) {
  return (
    <ErrorBoundary>
      <TriggerEditorFrontendInternal {...props} />
    </ErrorBoundary>
  );
}

type TriggerEditorProps = {
  initialLiveTriggerId: string;
  mode: "view" | "edit";
  triggerTrack: DiscordTriggersRpc;
  editView: boolean;
};

/**
 *
 * @param `{ initialTriggerId }: we take an initial trigger id here since even when updating a trigger,
 *  we create a new trigger. So the resulting trigger id will be different every time when the user clicks
 *  save. But we want to keep this invisible to the user. To the user, he's still updating the same trigger.
 * @returns
 */
export default function TriggerEditor({
  initialLiveTriggerId,
  mode,
  triggerTrack,
  editView,
}: TriggerEditorProps) {
  const { loadingPlanTier, userPlanTier } = usePaywallStateDispatch();
  const navigate = useNavigate();
  const [currentTrigger, setCurrentTrigger] = useState<TriggerForEditor | null>(
    null
  );

  // const {
  //   data,
  //   isLoading,
  //   isError: isLoadingError,
  // } = useTrigger(initialLiveTriggerId, mode === "edit" ? "draft" : "live");
  const [currentTab, setCurrentTab] = useState("edit");
  const { data, isError: isLoadingError } = useTrigger(
    initialLiveTriggerId,
    mode === "edit" ? "draft" : "live"
  );

  const triggerDraft = data?.trigger ? data.trigger : null;
  // initial validation errors are obtained here, if they exist.
  //TODO FIX THIS
  const validationErrors = data?.validationErrors
    ? data.validationErrors.map((error) => {
        return typeof error === "object" ? error : { message: error };
      })
    : null;

  const mutation = useTriggerMutation();
  const persistedTriggerId = useRef(triggerDraft?.id);
  const onPersist: OnPersistTrigger = useCallback(
    async (kind: PersistKind, updatedTrigger) => {
      const [persistedTrigger, response] = await mutation.mutateAsync({
        kind,
        trigger: updatedTrigger as TriggerCreate,
        parentId: persistedTriggerId.current ?? triggerDraft?.id,
      });
      persistedTriggerId.current = persistedTrigger.id;
      return [persistedTrigger, response];
    },
    [triggerDraft?.id]
  );

  const previousTrigger = useDefinedPreviousValue(triggerDraft);
  // This is to prevent the editor from being reset after save
  const triggerToRender = triggerDraft ?? previousTrigger;
  // const [errors, setErrors] = useState<string[]>([]);

  const getUpdatedAtDate = () => {
    let updatedAtDate = currentTrigger?.updatedAt;
    if (typeof updatedAtDate === "string") {
      updatedAtDate = parseISO(updatedAtDate);
    }
    const updatedAt = updatedAtDate ? formatDistanceToNow(updatedAtDate) : "-";
    return updatedAt;
  };

  // useEffect(() => {
  //   if (isLoadingError) {
  //     setErrors(["Failed to load the trigger. Please try again later."]);
  //   } else if (mutation.error) {
  //     setErrors([TriggerEditorErrorHandler(mutation.error.message)]);
  //   } else {
  //     setErrors([]);
  //   }
  // }, [isLoadingError, mutation.error]);
  //TODO: type this.

  useEffect(() => {
    if (editView) {
      setCurrentTab("edit");
    } else {
      setCurrentTab("track");
    }
  }, [editView]);

  if (!triggerToRender) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <>
      {/* I don't think we should add this spinner which makes the UI feel slow.  */}
      <div
        className="relative w-full"
        style={currentTab === "edit" ? { height: "82vh" } : { height: "auto" }}
      >
        <h3 className="absolute font-bold text-xl -top-14 text-left">
          {currentTrigger?.name || triggerToRender.name}{" "}
          {currentTab === "edit" && (
            <span className="ml-2 text-xs text-gray-500 font-medium">
              (Updated {getUpdatedAtDate()} ago)
            </span>
          )}
        </h3>

        {/* window.analytics.track(`Switched to ${tab} tab in settings page`, {platform: currentPlatform,});*/}

        {/* Had to make my own tab because couldn't find how to override the gray bottom border from antd Tabs */}
        <div className="w-full flex justify-center relative -top-6 mb-1">
          <div
            className={clsx(
              "trigger-editor-custom-tabs border-dots-vibrant-orange",
              {
                active: currentTab === "edit",
              }
            )}
            onClick={() => {
              if (currentTab !== "edit") {
                setCurrentTab("edit");
                navigate("edit");
              }
            }}
          >
            <EditOutlined /> <span>Edit</span>
          </div>
          <div
            className={clsx(
              "trigger-editor-custom-tabs border-dots-vibrant-orange",
              {
                active: currentTab === "track",
              }
            )}
            onClick={() => {
              if (currentTab !== "track") {
                setCurrentTab("track");
                navigate(`/automation/${initialLiveTriggerId}`);
              }
            }}
          >
            <LineChartOutlined /> <span>Track</span>
          </div>
        </div>
        {currentTab === "edit" ? (
          <TriggerEditorFrontend
            initialTrigger={
              currentTrigger && mode !== "edit"
                ? currentTrigger
                : triggerToRender
            }
            onPersist={onPersist}
            isLoadingError={isLoadingError || !!mutation.error}
            initialValidationErrors={validationErrors ? validationErrors : []}
            initialLiveTriggerId={initialLiveTriggerId}
            error={mutation.error}
            setCurrentTrigger={setCurrentTrigger}
          />
        ) : (
          <Paywall
            free={<DiscordTriggerTrack trigger={triggerTrack} />}
            pro={
              <DiscordTriggerTrack
                trigger={triggerTrack}
                disableExportButton={false}
              />
            }
            business={
              <DiscordTriggerTrack
                trigger={triggerTrack}
                disableExportButton={false}
                blockExports={{ oneMonth: "", filteredData: "" }}
                oneClickExport
              />
            }
            tier={userPlanTier}
            loading={loadingPlanTier}
          />
        )}
      </div>
    </>
  );
}
